import { useEffect, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { LogIn } from 'lucide-react';
import ThemeToggle from '../../components/Header/ThemeToggle';
import Logo from '../../images/logo/logo.png';
import { CHANNEL_URL, SITE, mailto } from './site';

/** Sets the tab title and meta description for a public page. */
export function usePublicMeta(title: string, description: string) {
  useEffect(() => {
    document.title = title;
    const meta = document.querySelector('meta[name="description"]');
    const before = meta?.getAttribute('content');
    meta?.setAttribute('content', description);
    return () => { if (meta && before != null) meta.setAttribute('content', before); };
  }, [title, description]);
}

export function Eyebrow({ children, onNavy = false }: { children: ReactNode; onNavy?: boolean }) {
  return (
    <div className={`font-mono text-[11.5px] font-semibold uppercase tracking-[0.1em] ${onNavy ? 'text-accent' : 'text-accent-ink'}`}>
      {children}
    </div>
  );
}

export function PlayButton({ tone = 'light' }: { tone?: 'light' | 'dark' }) {
  const face = tone === 'light'
    ? 'bg-white text-navy hover:bg-mist-fixed'
    : 'bg-navy text-white hover:bg-navy-2 dark:bg-white dark:text-navy dark:hover:bg-mist-fixed';
  return (
    <a href={SITE.playUrl} target="_blank" rel="noopener noreferrer"
      className={`inline-flex items-center gap-3 rounded-[14px] px-5 py-3 font-semibold no-underline transition ${face}`}>
      <svg aria-hidden viewBox="0 0 24 24" className="h-6 w-6"><path fill="currentColor" d="M5 3.5v17a1 1 0 0 0 1.5.86l14-8.5a1 1 0 0 0 0-1.72l-14-8.5A1 1 0 0 0 5 3.5Z" /></svg>
      <span className="flex flex-col text-left leading-tight">
        <span className="text-[11px] font-medium opacity-75">Get it on</span>
        <span className="text-[17px]">Google Play</span>
      </span>
    </a>
  );
}

const NAV = [
  { href: '/#students', label: 'For students' },
  { href: '/#schools', label: 'For schools' },
  { href: '/tutorials', label: 'Tutorials' },
  { href: '/#demo', label: 'Book a demo' },
  { href: '/#team', label: 'Team' },
  { href: '/#contact', label: 'Contact' },
];

export default function PublicLayout({ children }: { children: ReactNode }) {
  return (
    <div className="min-h-screen bg-ground font-sans text-ink">
      <header className="sticky top-0 z-30 border-b border-white/10 bg-navy/95 text-white backdrop-blur">
        <div className="mx-auto flex h-16 max-w-[1200px] items-center gap-4 px-4 sm:px-6">
          <Link to="/" className="flex items-center gap-2.5 text-white no-underline">
            <img src={Logo} alt="" className="h-9 w-9 rounded-[10px]" />
            <span className="font-display text-xl font-extrabold tracking-[-0.02em]">YADA</span>
          </Link>
          <nav aria-label="Main" className="ml-6 hidden gap-1 md:flex">
            {NAV.map((n) => (
              <a key={n.href} href={n.href}
                className="rounded-[10px] px-3 py-2 text-sm font-medium text-on-navy no-underline hover:bg-white/10 hover:text-white">
                {n.label}
              </a>
            ))}
          </nav>
          <div className="ml-auto flex items-center gap-2">
            <ThemeToggle className="!h-10 !w-10 !border-white/15 !bg-white/5 !text-white hover:!bg-white/10" />
            <Link to="/login"
              className="inline-flex h-10 items-center gap-2 rounded-[12px] bg-accent px-4 text-sm font-semibold text-white no-underline hover:brightness-105">
              <LogIn size={16} strokeWidth={2} />
              <span className="hidden sm:inline">School sign in</span>
              <span className="sm:hidden">Sign in</span>
            </Link>
          </div>
        </div>
      </header>

      <main>{children}</main>

      <footer className="border-t border-line bg-surface">
        <div className="mx-auto grid max-w-[1200px] gap-8 px-4 py-12 sm:px-6 md:grid-cols-[1.4fr_1fr_1fr]">
          <div>
            <div className="flex items-center gap-2.5">
              <img src={Logo} alt="" className="h-8 w-8 rounded-[9px]" />
              <span className="font-display text-lg font-extrabold">YADA</span>
            </div>
            <p className="mt-3 max-w-[360px] text-sm text-muted">
              AI exam preparation for Ethiopian students and an early-warning panel for their schools.
              Built by {SITE.company} in {SITE.city}.
            </p>
          </div>
          <div>
            <div className="font-mono text-[11px] font-semibold uppercase tracking-[0.08em] text-muted">Product</div>
            <ul className="mt-3 space-y-2 text-sm">
              <li><a className="text-ink no-underline hover:text-accent-ink" href={SITE.playUrl} target="_blank" rel="noopener noreferrer">Student app on Google Play</a></li>
              <li><Link className="text-ink no-underline hover:text-accent-ink" to="/login">School panel sign in</Link></li>
              <li><a className="text-ink no-underline hover:text-accent-ink" href="/#demo">Book a school demo</a></li>
              <li><Link className="text-ink no-underline hover:text-accent-ink" to="/tutorials">School panel tutorials</Link></li>
              <li><a className="text-ink no-underline hover:text-accent-ink" href={CHANNEL_URL} target="_blank" rel="noopener noreferrer">YouTube channel</a></li>
            </ul>
          </div>
          <div>
            <div className="font-mono text-[11px] font-semibold uppercase tracking-[0.08em] text-muted">Company</div>
            <ul className="mt-3 space-y-2 text-sm">
              <li><a className="text-ink no-underline hover:text-accent-ink" href="/#team">Team</a></li>
              <li><Link className="text-ink no-underline hover:text-accent-ink" to="/privacy">Privacy policy</Link></li>
              <li><Link className="text-ink no-underline hover:text-accent-ink" to="/terms">Terms of use</Link></li>
              <li><a className="text-ink no-underline hover:text-accent-ink" href={mailto()}>{SITE.email}</a></li>
            </ul>
          </div>
        </div>
        <div className="border-t border-line">
          <div className="mx-auto flex max-w-[1200px] flex-wrap justify-between gap-2 px-4 py-5 text-xs text-muted sm:px-6">
            <span>© {new Date().getFullYear()} {SITE.company}. All rights reserved.</span>
            <span>{SITE.city}</span>
          </div>
        </div>
      </footer>
    </div>
  );
}
